import { Component, OnInit, Input } from '@angular/core';
import { DownloadsService } from '../../services/downloads.service';
import { CommonModule } from '@angular/common';
import { SvgIconComponent } from 'angular-svg-icon';

/**
 * File list component for displaying downloadable files and products.
 *
 * @remarks Renders a list of files or products based on the specified type.
 * Integrates with the DownloadsService to fetch and display download items
 * with proper icons and formatted names. Supports both downloads and products
 * display modes.
 */
@Component({
  selector: 'app-file-list',
  templateUrl: './file-list.component.html',
  styleUrls: ['./file-list.component.scss'],
  standalone: true,
  imports: [CommonModule, SvgIconComponent],
})
export class FileListComponent implements OnInit {
  /**
   * Creates an instance of FileListComponent.
   *
   * @param downloadService - Service for fetching downloads and product data
   */
  constructor(private downloadService: DownloadsService) {}

  /** @remarks Type of files to display - either 'downloads' or 'products' */
  @Input() type: string;

  /** @remarks Array of files or products to be displayed in the list */
  public files: any[];

  /**
   * Gets the current loading status from the downloads service.
   *
   * @remarks Provides reactive access to the service's loading state
   * for displaying loading indicators or handling UI state changes.
   *
   * @returns The current download service status
   */
  get status() {
    return this.downloadService.status;
  }

  /**
   * Component initialization lifecycle hook.
   *
   * @remarks Fetches downloads or products data from the service based on
   * the component's type input. Updates the files array when data is received.
   */
  ngOnInit() {
    this.downloadService.getDownloads().subscribe({
      next: () => {
        this.files = this.type === 'downloads' ? this.downloadService.downloads : this.downloadService.products;
      },
    });
  }

  /**
   * Normalizes file names by replacing underscores with spaces.
   *
   * @remarks Formats file names for better readability by converting
   * underscores to spaces, making them more user-friendly in the UI.
   *
   * @param string - The string to normalize
   * @returns The normalized string with underscores replaced by spaces
   */
  normalize(string: string) {
    return string.replace(/_/g, ' ');
  }
}
